/**
 * O ambiente da cena: câmera, luz e arena. three.js PURO (ADR-0003).
 * NEVER importe react aqui — o dependency-cruiser falha o build.
 *
 * Cor NEVER é literal: vem de `@game/design` (ART-DIRECTION M1), e os valores de
 * mundo vêm de `@game/gamedata` (invariante 6). Este arquivo NEVER decide número.
 *
 * O que mora aqui é o que NEVER muda por frame. O que muda por jogador é do `World`.
 */
import { tokens } from '@game/design';
import { balance } from '@game/gamedata';
import {
  CircleGeometry,
  Color,
  DirectionalLight,
  HemisphereLight,
  Mesh,
  MeshLambertMaterial,
  PerspectiveCamera,
  RingGeometry,
  Scene,
} from 'three';

export interface SceneBundle {
  readonly scene: Scene;
  readonly camera: PerspectiveCamera;
  /** Reajusta a câmera ao tamanho em CSS pixels. NEVER confunda com escala de render. */
  setViewport(width: number, height: number): void;
  dispose(): void;
}

/** Ângulo da câmera. Alto o bastante para a arena inteira caber em retrato. */
const CAMERA = { fov: 55, near: 0.1, far: 200, height: 26, back: 22 };
/** Folga além da borda da arena, para a linha do limite NEVER encostar na tela. */
const VIEW_MARGIN = 1.15;

export function createScene(): SceneBundle {
  const scene = new Scene();
  scene.background = new Color(tokens.color.role.canvasBg);

  const camera = new PerspectiveCamera(CAMERA.fov, 1, CAMERA.near, CAMERA.far);
  camera.position.set(0, CAMERA.height, CAMERA.back);
  camera.lookAt(0, 0, 0);

  // Duas luzes e ponto final: sombra é decisão por tier (PERF.yaml) e entra com o
  // controlador de qualidade, NEVER aqui.
  const sky = new HemisphereLight(
    new Color(tokens.color.role.textSecondary),
    new Color(tokens.color.role.surface),
    1.1,
  );
  const sun = new DirectionalLight(new Color(tokens.color.role.textPrimary), 0.8);
  sun.position.set(6, 18, 8);
  scene.add(sky, sun);

  const radius = balance.arena.radius;
  const floorGeometry = new CircleGeometry(radius, 64);
  // `surfaceRaised`, não `surface`: contra o fundo do canvas o chão sumia na tela.
  const floorMaterial = new MeshLambertMaterial({
    color: new Color(tokens.color.role.surfaceRaised),
  });
  const floor = new Mesh(floorGeometry, floorMaterial);
  floor.rotation.x = -Math.PI / 2;
  scene.add(floor);

  // A borda é informação de gameplay, não enfeite: é o limite que o sim aplica.
  const edgeGeometry = new RingGeometry(radius - 0.35, radius, 64);
  const edgeMaterial = new MeshLambertMaterial({
    color: new Color(tokens.color.role.accentText),
  });
  const edge = new Mesh(edgeGeometry, edgeMaterial);
  edge.rotation.x = -Math.PI / 2;
  // Acima do chão de propósito: coplanar com o piso daria z-fighting em GPU móvel.
  edge.position.y = 0.02;
  scene.add(edge);

  return {
    scene,
    camera,
    setViewport(width, height) {
      if (width <= 0 || height <= 0) return;
      camera.aspect = width / height;
      // Em retrato a arena sai pela lateral se o fov não abrir. Mantém a arena
      // inteira visível em qualquer proporção, NEVER corta o limite.
      const needed = 2 * Math.atan((radius * VIEW_MARGIN) / CAMERA.back);
      const vertical = camera.aspect >= 1 ? needed : needed / camera.aspect;
      camera.fov = Math.min(100, Math.max(CAMERA.fov, (vertical * 180) / Math.PI));
      camera.updateProjectionMatrix();
    },
    dispose() {
      floorGeometry.dispose();
      floorMaterial.dispose();
      edgeGeometry.dispose();
      edgeMaterial.dispose();
    },
  };
}
